import React, { useState, useContext, useEffect, useRef } from "react";
import { useHistory, useLocation } from "react-router-dom";
import {
  Menu,
  Sun,
  Moon,
  UserCircle,
  LogOut,
  X,
  HardDrive,
  Plug,
  Search,
  HelpCircle,
  MoreHorizontal,
} from "lucide-react";
import Tooltip from "@material-ui/core/Tooltip";
import ClickAwayListener from "@material-ui/core/ClickAwayListener";
import useMediaQuery from "@material-ui/core/useMediaQuery";
import { useTheme } from "@material-ui/core/styles";

import MainListItems from "./MainListItems";
import NotificationsPopOver from "../components/NotificationsPopOver";
import PendingActionsPopover from "../components/PendingActionsPopover";
import NotificationsVolume from "../components/NotificationsVolume";
import LeadSlaAlert from "../components/LeadSlaAlert";
import PauseButton from "../components/PauseButton";
import UserModal from "../components/UserModal";
import DiskUsageAlert from "../components/DiskUsageAlert";
import AIProviderHealthAlert from "../components/AIProviderHealthAlert";
import { AuthContext } from "../context/Auth/AuthContext";
import { WhatsAppsContext } from "../context/WhatsApp/WhatsAppsContext";
import { isAdminProfile } from "../helpers/userPermissions";
import { hasMenuPermission, menuKeyForPath } from "../helpers/menuPermissions";
import BackdropLoading from "../components/BackdropLoading";
import { i18n } from "../translate/i18n";
import toastError from "../errors/toastError";
import TasksPopover from "../components/TasksPopover";
import CallbackWidget from "../components/CallbackWidget";
import ColorModeContext from "../layout/themeContext";
import { getBackendUrl } from "../config";
import Logo from "../components/Logo/";
import IconButtonWithTooltip from "../components/ui/IconButtonWithTooltip";
import "./navbar.css";
import api from "../services/api";
import { resolveUserAvatar } from "../utils/avatar";
// SoftphoneWidget removed - replaced by SoftphoneSidebarButton
import { VoipProvider } from "../context/VoipContext";
import MessageSearchModal from "../components/MessageSearchModal";
import { MessageSearchProvider } from "../context/MessageSearch/MessageSearchContext";

const backendUrl = getBackendUrl();

const LoggedInLayout = ({ children }) => {
  const history = useHistory();
  const location = useLocation();
  const [userModalOpen, setUserModalOpen] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);
  const [mobileDrawerOpen, setMobileDrawerOpen] = useState(false);
  const [searchAnchorEl, setSearchAnchorEl] = useState(null);
  const [moreMenuOpen, setMoreMenuOpen] = useState(false);

  // Torna a linha inteira do menu "Mais opções" clicável: cliques no label/área
  // vazia são encaminhados ao botão do widget interno (que já tem o handler real).
  const handleMoreItemClick = (e) => {
    if (e.target.closest("button")) return;
    const btn = e.currentTarget.querySelector("button");
    if (btn) btn.click();
  };
  const [escArmedToGoTickets, setEscArmedToGoTickets] = useState(false);
  const { handleLogout, loading } = useContext(AuthContext);
  const { user, socket } = useContext(AuthContext);
  const { whatsApps } = useContext(WhatsAppsContext);
  const colorModeContext = useContext(ColorModeContext);
  const theme = useTheme();
  // Mobile (<600px sm): esconde controles redundantes do cluster do navbar.
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const [connectionWarning, setConnectionWarning] = useState(false);
  const [diskUsage, setDiskUsage] = useState(null);

  // ✅ Permissões (mesma lógica do menu): admin vê tudo, demais dependem de user.menuPermissions
  const canAccessStorageManagement = hasMenuPermission(
    user,
    menuKeyForPath("/storage-management")
  );

  const [volume, setVolume] = useState(() => {
    const savedVolume = localStorage.getItem("volume");
    if (savedVolume === null) return 1;
    const parsedVolume = Number(savedVolume);
    return isNaN(parsedVolume) ? 1 : Math.max(0, Math.min(1, parsedVolume));
  });

  useEffect(() => {
    localStorage.setItem("volume", volume);
  }, [volume]);


  // 🔍 Atalho Ctrl+K para busca
  const searchButtonRef = useRef(null);
  useEffect(() => {
    const handleKeyDown = (e) => {
      if ((e.ctrlKey || e.metaKey) && e.key === 'k') {
        e.preventDefault();
        setSearchAnchorEl(searchButtonRef.current);
      }
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, []);

  // ⎋ ESC global:
  // - Sem modal aberto: ESC volta imediatamente para /tickets
  // - Com modal aberto: 1x ESC fecha modal (comportamento nativo) e "arma" voltar para tickets
  // - Após fechar modal: próximo ESC volta para /tickets
  useEffect(() => {
    const isElementVisible = (el) => {
      if (!el) return false;
      // Alguns componentes ficam montados mas "hidden"/aria-hidden
      if (el.getAttribute?.("aria-hidden") === "true") return false;
      if (el.classList?.contains("MuiModal-hidden")) return false;
      const style = window.getComputedStyle(el);
      if (style.display === "none" || style.visibility === "hidden") return false;
      // getClientRects funciona melhor que offsetParent para elementos fixed
      if (el.getClientRects && el.getClientRects().length === 0) return false;
      return true;
    };

    const isAnyModalOpen = () => {
      // Material-UI Dialog/Modal (somente visíveis)
      const candidates = [
        ...document.querySelectorAll(".MuiDialog-root"),
        ...document.querySelectorAll(".MuiModal-root"),
        ...document.querySelectorAll('[role="dialog"]'),
        ...document.querySelectorAll('[aria-modal="true"]'),
      ];

      return candidates.some(isElementVisible);
    };

    const handleEsc = (e) => {
      if (e.key !== "Escape") return;

      const modalOpen = isAnyModalOpen();

      if (modalOpen) {
        // Deixa o modal lidar com o ESC (não preventDefault).
        setEscArmedToGoTickets(true);
        return;
      }

      // Sem modal aberto: ESC sempre volta para tickets
      setEscArmedToGoTickets(false);
      const path = location?.pathname || "";
      if (path.startsWith("/tickets")) return;
      if (path.startsWith("/flows/")) return;
      if (path.startsWith("/whatsapp-flows/")) return;
      history.push("/tickets");
    };

    // Capture para não perder o ESC caso algum componente pare a propagação
    window.addEventListener("keydown", handleEsc, true);
    return () => window.removeEventListener("keydown", handleEsc, true);
  }, [escArmedToGoTickets, history, location?.pathname]);

  // Se o usuário navega manualmente, desarma para evitar surpresa
  useEffect(() => {
    setEscArmedToGoTickets(false);
  }, [location?.pathname]);

  const [profileUrl, setProfileUrl] = useState(null);
  const userMenuRef = useRef(null);
  const avatarRef = useRef(null);

  const isDarkMode = colorModeContext.mode === "dark";

  useEffect(() => {
    const companyId = user.companyId;
    const userId = user.id;
    // ✅ FIX: Verificar se socket existe antes de usar
    if (companyId && socket) {
      const ImageUrl = user.profileImage;
      setProfileUrl(resolveUserAvatar(ImageUrl, companyId, backendUrl));

      const onCompanyAuthLayout = (data) => {
        // Política "1 sessão por plataforma": o web só reage a logins web.
        // `platform` ausente (clients/servidor legados) é tratado como "web"
        // para preservar o comportamento histórico. Logins de app (ios/android)
        // são ignorados aqui — eles derrubam apenas outras sessões mobile.
        const otherPlatform = (data?.platform || "web").toLowerCase();
        if (otherPlatform !== "web") return;
        if (data.user.id === +userId) {
          toastError("Sua conta foi acessada em outro computador.");
          setTimeout(() => {
            localStorage.clear();
            window.location.reload();
          }, 1000);
        }
      };

      socket.on(`company-${companyId}-auth`, onCompanyAuthLayout);

      socket.emit("userStatus");
      const interval = setInterval(() => {
        socket.emit("userStatus");
      }, 1000 * 60 * 5);

      socket.on("user-profile-update", (data) => {
        if (data.userId === userId) {
          setProfileUrl(resolveUserAvatar(data.profileImage, data.companyId, backendUrl));
        }
      });

      return () => {
        socket.off(`company-${companyId}-auth`, onCompanyAuthLayout);
        clearInterval(interval);
        socket.off("user-profile-update");
      };
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [socket]);

  useEffect(() => {
    function handleClickOutside(event) {
      if (
        userMenuRef.current &&
        !userMenuRef.current.contains(event.target) &&
        avatarRef.current &&
        !avatarRef.current.contains(event.target)
      ) {
        setMenuOpen(false);
      }
    }

    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [userMenuRef, avatarRef]);

  useEffect(() => {
    const delayDebounceFn = setTimeout(() => {
      if (whatsApps.length > 0) {
        const offlineWhats = whatsApps.filter((whats) => {
          return (
            whats.status === "qrcode" ||
            whats.status === "PAIRING" ||
            whats.status === "DISCONNECTED" ||
            whats.status === "TIMEOUT" ||
            whats.status === "OPENING"
          );
        });
        if (offlineWhats.length > 0) {
          setConnectionWarning(true);
        } else {
          setConnectionWarning(false);
        }
      }
    }, 2000);
    return () => clearTimeout(delayDebounceFn);
  }, [whatsApps]);

  useEffect(() => {
    if (!user?.companyId || user?.profile !== "admin") {
      return;
    }

    const fetchDiskUsage = async () => {
      try {
        const { data } = await api.get(
          `/companies/${user.companyId}/disk-usage`
        );
        setDiskUsage(data);
      } catch (error) {
        console.error("Erro ao buscar uso de disco:", error);
      }
    };

    fetchDiskUsage();

    const interval = setInterval(fetchDiskUsage, 1000 * 60 * 5);

    return () => {
      clearInterval(interval);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user?.companyId, user?.profile]); // Usar apenas propriedades específicas

  const toggleMenu = () => {
    setMenuOpen(!menuOpen);
  };

  const handleOpenUserModal = () => {
    setUserModalOpen(true);
    setMenuOpen(false);
  };



  const handleClickLogout = () => {
    setMenuOpen(false);
    handleLogout();
  };

  const toggleMobileDrawer = () => {
    setMobileDrawerOpen(!mobileDrawerOpen);
  };

  const closeMobileDrawer = () => {
    setMobileDrawerOpen(false);
  };

  const getConnectionStatusClass = () => {
    if (!connectionWarning) return "online";
    if (
      whatsApps.some(
        (whats) => whats.status === "DISCONNECTED" || whats.status === "TIMEOUT"
      )
    ) {
      return "offline";
    }
    return "warning";
  };

  const getConnectionStatusText = () => {
    const disconnectedCount = whatsApps.filter(
      (whats) =>
        whats.status === "DISCONNECTED" ||
        whats.status === "TIMEOUT" ||
        whats.status === "qrcode" ||
        whats.status === "PAIRING" ||
        whats.status === "OPENING"
    ).length;

    const connectedCount = whatsApps.length - disconnectedCount;

    // ✅ Navbar deve exibir apenas a contagem (sem "Online/Desconectado/Atenção")
    return `${connectedCount}/${whatsApps.length}`;
  };

  const getConnectionStatusTooltipText = () => {
    const disconnectedCount = whatsApps.filter(
      (whats) =>
        whats.status === "DISCONNECTED" ||
        whats.status === "TIMEOUT" ||
        whats.status === "qrcode" ||
        whats.status === "PAIRING" ||
        whats.status === "OPENING"
    ).length;

    const connectedCount = whatsApps.length - disconnectedCount;

    if (!connectionWarning)
      return `${whatsApps.length}/${whatsApps.length} ${i18n.t(
        "connections.online"
      )}`;

    if (
      whatsApps.some(
        (whats) => whats.status === "DISCONNECTED" || whats.status === "TIMEOUT"
      )
    ) {
      return `${connectedCount}/${whatsApps.length} ${i18n.t(
        "connections.offline"
      )}`;
    }

    return `${connectedCount}/${whatsApps.length} ${i18n.t(
      "connections.warning"
    )}`;
  };

  const getQuotaBarColor = () => {
    if (!diskUsage || !diskUsage.diskQuota) return "success";

    const percentage = Math.round(
      (diskUsage.diskUsage / diskUsage.diskQuota) * 100
    );

    if (percentage >= 95) return "critical"; // Pulsa vermelho
    if (percentage >= 90) return "error";    // Vermelho
    if (percentage >= 51) return "warning";  // Amarelo
    return "success";                        // Verde
  };

  const getQuotaPercentage = () => {
    if (!diskUsage || !diskUsage.diskQuota) return 0;
    return Math.min(
      Math.round((diskUsage.diskUsage / diskUsage.diskQuota) * 100),
      100
    );
  };

  if (loading) {
    return <BackdropLoading />;
  }

  return (
    <MessageSearchProvider>
    <VoipProvider>
    <div className={`app-container ${isDarkMode ? "dark" : "light"}`}>
      {/* ♿ Skip-link: visualmente escondido, aparece ao focar via teclado (Tab).
          Permite pular a navbar e ir direto ao conteúdo principal. */}
      <a href="#main-content" className="skip-to-content">
        Pular para o conteúdo
      </a>
      <header className="navbar">
        <div className="navbar-container">
          <div className="navbar-left">
            <button
              className="mobile-menu-button"
              onClick={toggleMobileDrawer}
              aria-label="Menu"
            >
              <Menu className="icon" />
            </button>

            <div className="navbar-logo desktop-only">
              <Logo size="medium" />
            </div>

            <div className="navbar-menu">
              <div className="desktop-menu">
                <MainListItems />
              </div>
            </div>
          </div>

          <div className="navbar-actions">
            <div className="version-container"></div>

            {/* Bloco compacto: usuário + conexão + disco (mais próximos entre si) */}
            <div className="navbar-status-cluster">

              {/* Chip de nome: redundante com o avatar/menu em mobile — oculto em <600px */}
              {!isMobile && user?.email && (
                <Tooltip title="Usuário logado" placement="bottom" arrow>
                  <div
                    className="navbar-user-email-direct"
                    style={{ 
                      display: 'flex', 
                      alignItems: 'center',
                      gap: '6px',
                      height: '26px',
                      backgroundColor: isDarkMode ? 'rgba(96, 165, 250, 0.1)' : 'rgba(30, 58, 138, 0.06)', 
                      padding: '0 10px', 
                      borderRadius: '6px',
                      flexShrink: 0,
                      cursor: 'pointer',
                      transition: 'all 0.2s ease'
                    }}
                    onClick={handleOpenUserModal}
                    onMouseEnter={(e) => {
                      e.currentTarget.style.backgroundColor = isDarkMode ? 'rgba(96, 165, 250, 0.2)' : 'rgba(30, 58, 138, 0.12)';
                      e.currentTarget.style.transform = 'translateY(-1px)';
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.backgroundColor = isDarkMode ? 'rgba(96, 165, 250, 0.1)' : 'rgba(30, 58, 138, 0.06)';
                      e.currentTarget.style.transform = 'translateY(0)';
                    }}
                  >
                    <UserCircle style={{ width: '14px', height: '14px', color: isDarkMode ? '#93c5fd' : '#1e3a8a', flexShrink: 0, opacity: 0.8 }} />
                    <span 
                      style={{ 
                        fontSize: '11px', 
                        fontWeight: '500', 
                        color: isDarkMode ? '#93c5fd' : '#1e3a8a', 
                        whiteSpace: 'nowrap',
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        maxWidth: '120px',
                        display: 'inline-block',
                        opacity: 0.9
                      }}
                      title={user.name || user.email}
                    >
                      {user.name || "Sem nome"}
                    </span>
                  </div>
                </Tooltip>
              )}

            {whatsApps.length > 0 && (
              <Tooltip
                title={
                  i18n.t("connections.title") + ": " + getConnectionStatusTooltipText()
                }
                placement="bottom"
                arrow
              >
                <div
                  className={`connection-status-container connection-chip ${getConnectionStatusClass()}`}
                  aria-label={i18n.t("connections.title")}
                >
                  <Plug
                    size={16}
                    className={`connection-status-icon ${getConnectionStatusClass()}`}
                  />
                  <span className="connection-status-label">Conexões</span>
                  <span
                    className={`connection-status-text ${getConnectionStatusClass()}`}
                  >
                    {getConnectionStatusText()}
                  </span>
                </div>
              </Tooltip>
            )}

            {isAdminProfile(user) &&
              diskUsage &&
              diskUsage.diskQuota > 0 && (
                <Tooltip
                  title={
                    canAccessStorageManagement
                      ? `Uso de disco: ${getQuotaPercentage()}% (${diskUsage.formattedDiskUsage} de ${diskUsage.formattedDiskQuota}) • Clique para gerenciar`
                      : `Uso de disco: ${getQuotaPercentage()}% (${diskUsage.formattedDiskUsage} de ${diskUsage.formattedDiskQuota})`
                  }
                  placement="bottom"
                  arrow
                >
                  <div
                    className="disk-usage-icon-container"
                    role={canAccessStorageManagement ? "button" : undefined}
                    aria-label={canAccessStorageManagement ? "Gerenciar armazenamento" : "Uso de disco"}
                    tabIndex={canAccessStorageManagement ? 0 : undefined}
                    style={{ cursor: canAccessStorageManagement ? "pointer" : "default" }}
                    onClick={() => {
                      if (!canAccessStorageManagement) return;
                      history.push("/storage-management");
                    }}
                    onKeyDown={(e) => {
                      if (!canAccessStorageManagement) return;
                      if (e.key === "Enter" || e.key === " ") {
                        e.preventDefault();
                        history.push("/storage-management");
                      }
                    }}
                  >
                    <HardDrive
                      size={20}
                      className={`disk-icon ${getQuotaBarColor()}`}
                    />
                  </div>
                </Tooltip>
              )}
            </div>

            <div className="navbar-divider" aria-hidden="true"></div>

            {/* 🔍 Busca de Mensagens */}
            {hasMenuPermission(
              user,
              menuKeyForPath("/help-center/:categoryId?/:articleId?")
            ) && (
            <div className="help-center-button">
              <IconButtonWithTooltip
                icon={HelpCircle}
                tooltip={i18n.t("helpCenter.title")}
                onClick={() => history.push("/help-center")}
                aria-label={i18n.t("helpCenter.title")}
              />
            </div>
            )}

            <div className="search-button" ref={searchButtonRef}>
              <IconButtonWithTooltip
                icon={Search}
                tooltip="Buscar mensagens (Ctrl+K)"
                onClick={(e) => setSearchAnchorEl(e.currentTarget.parentElement)}
                aria-label="Buscar mensagens"
              />
            </div>

            <div className="theme-toggle">
              <IconButtonWithTooltip
                icon={isDarkMode ? Sun : Moon}
                tooltip={isDarkMode ? "Modo claro" : "Modo escuro"}
                onClick={colorModeContext.toggleColorMode}
                aria-label={isDarkMode ? "Modo claro" : "Modo escuro"}
              />
            </div>

            <div className="navbar-divider" aria-hidden="true"></div>

            {user.id && (
              <div className="pause-button">
                <PauseButton />
              </div>
            )}

            {user.id && (
              <div className="notifications">
                <NotificationsPopOver volume={volume} />
              </div>
            )}

            {/* 🎨 Fase 2 — "Mais opções": agrupa os ícones menos usados.
                ClickAwayListener com guarda: ignora cliques dentro de popovers MUI
                (slider de volume, listas de tarefas/callbacks), senão o menu fecharia
                ao interagir com o popover de dentro. */}
            <ClickAwayListener
              onClickAway={(e) => {
                if (
                  e.target.closest &&
                  e.target.closest(
                    ".MuiPopover-root, .MuiModal-root, .MuiPopper-root, .MuiMenu-root, .MuiTooltip-popper"
                  )
                ) {
                  return;
                }
                setMoreMenuOpen(false);
              }}
            >
              <div className="navbar-more">
                <IconButtonWithTooltip
                  icon={MoreHorizontal}
                  tooltip="Mais opções"
                  onClick={() => setMoreMenuOpen((o) => !o)}
                  aria-label="Mais opções"
                  aria-haspopup="true"
                  aria-expanded={moreMenuOpen}
                />
                {moreMenuOpen && (
                  <div className="navbar-more-menu">
                    <div className="navbar-more-item" onClick={handleMoreItemClick}>
                      <div className="notifications-volume">
                        <NotificationsVolume setVolume={setVolume} volume={volume} />
                      </div>
                      <span className="navbar-more-label">Volume das notificações</span>
                    </div>

                    {user.id && (
                      <div className="navbar-more-item" onClick={handleMoreItemClick}>
                        <div className="pending-actions">
                          <PendingActionsPopover />
                        </div>
                        <span className="navbar-more-label">Ações pendentes</span>
                      </div>
                    )}

                    {user.id && user.companyId && (
                      <div className="navbar-more-item" onClick={handleMoreItemClick}>
                        <div className="lead-sla-alerts">
                          <LeadSlaAlert />
                        </div>
                        <span className="navbar-more-label">SLA de leads</span>
                      </div>
                    )}

                    <div className="navbar-more-item" onClick={handleMoreItemClick}>
                      <div className="tasks-popover">
                        <TasksPopover />
                      </div>
                      <span className="navbar-more-label">Tarefas</span>
                    </div>

                    {/* CallbackWidget só renderiza com VoIP habilitado — mesma condição
                        aqui, senão sobra o label "Callbacks" sem ícone e sem ação */}
                    {!!(user?.voipEnabled && user?.voipExtension) && (
                      <div className="navbar-more-item" onClick={handleMoreItemClick}>
                        <div className="callback-widget">
                          <CallbackWidget />
                        </div>
                        <span className="navbar-more-label">Callbacks</span>
                      </div>
                    )}
                  </div>
                )}
              </div>
            </ClickAwayListener>

            <div className="navbar-divider" aria-hidden="true"></div>

            <div className="user-profile">
              <Tooltip
                title={i18n.t("mainDrawer.appBar.user.profile") || "Perfil"}
                placement="bottom"
                arrow
              >
                <div
                  className="avatar-container"
                  ref={avatarRef}
                  onClick={toggleMenu}
                >
                  <div className="status-badge">
                    <img alt="Onticket" className="avatar" src={profileUrl} />
                  </div>
                </div>
              </Tooltip>
            </div>

            <UserModal
              open={userModalOpen}
              onClose={() => setUserModalOpen(false)}
              onImageUpdate={(newProfileUrl) => setProfileUrl(newProfileUrl)}
              userId={user?.id}
            />

            {/* 🔍 Popover de Busca de Mensagens */}
            <MessageSearchModal
              open={Boolean(searchAnchorEl)}
              anchorEl={searchAnchorEl}
              onClose={() => setSearchAnchorEl(null)}
            />

            {menuOpen && (
              <div className="user-menu" ref={userMenuRef}>
                <div className="user-menu-header">
                  <img
                    alt="Onticket"
                    src={profileUrl}
                    className="user-menu-avatar"
                  />
                  <div className="user-info">
                    <p className="user-name" title={user.name}>{user.name || "Sem nome"}</p>
                  </div>
                </div>
                <div className="menu-divider"></div>
                <button className="menu-item" onClick={handleOpenUserModal}>
                  <UserCircle className="menu-icon" />
                  <span>{i18n.t("mainDrawer.appBar.user.profile")}</span>
                </button>
                <button className="menu-item" onClick={handleClickLogout}>
                  <LogOut className="menu-icon" />
                  <span>{i18n.t("mainDrawer.appBar.user.logout")}</span>
                </button>
              </div>
            )}
          </div>
        </div>
      </header>

      {/* Drawer mobile */}
      {mobileDrawerOpen && (
        <div className="drawer-backdrop" onClick={toggleMobileDrawer}>
          <div className="drawer" onClick={(e) => e.stopPropagation()}>
            <div className="drawer-header">
              <div className="drawer-logo">
                <Logo size="medium" />
              </div>
              <button
                className="close-drawer-button"
                onClick={toggleMobileDrawer}
              >
                <X className="icon" />
              </button>
            </div>

            <div className="drawer-user">
              <div className="drawer-user-avatar">
                <img alt="Onticket" src={profileUrl} />
              </div>
              <div className="drawer-user-info">
                <p className="drawer-user-name" title={user.name}>{user.name || "Sem nome"}</p>
              </div>
            </div>

            <div className="menu-divider"></div>

            <div className="drawer-container">
              <MainListItems isMobile={true} closeDrawer={closeMobileDrawer} />
            </div>

            <div className="drawer-footer">
              <button
                className="drawer-footer-button"
                onClick={colorModeContext.toggleColorMode}
              >
                {isDarkMode ? (
                  <>
                    <Sun className="drawer-footer-icon" />
                    <span>{i18n.t("mainDrawer.appBar.theme.light")}</span>
                  </>
                ) : (
                  <>
                    <Moon className="drawer-footer-icon" />
                    <span>{i18n.t("mainDrawer.appBar.theme.dark")}</span>
                  </>
                )}
              </button>

              <button
                className="drawer-footer-button"
                onClick={handleClickLogout}
              >
                <LogOut className="drawer-footer-icon" />
                <span>{i18n.t("mainDrawer.appBar.user.logout")}</span>
              </button>
            </div>
          </div>
        </div>
      )}

      <main className="main-content" id="main-content" tabIndex={-1}>
        <DiskUsageAlert />
        <AIProviderHealthAlert />
        <div className="content-container">{children}</div>
      </main>

      {/* SoftphoneWidget removed - now using SoftphoneSidebarButton */}
    </div>
    </VoipProvider>
    </MessageSearchProvider>
  );
};

export default LoggedInLayout;
